import { useCallback, useRef, useState, type MutableRefObject } from 'react'
import { toast } from 'sonner'
import {
    AD_PLATFORM,
    AdPlatform,
    type AICreatePreselections,
} from '@storefronts/common-types'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { R } from '@/modules/api/actions'

export interface SearchQueryPayload {
    searchQuery: string
    currentLandingPageUrl?: string
}

function isValidUrl(url: string): boolean {
    try {
        new URL(url)
        return true
    } catch {
        return false
    }
}

export function canSubmitSearchQuery(
    searchQuery: string,
    currentLandingPageUrl: string,
): boolean {
    if (!searchQuery.trim()) return false
    const url = currentLandingPageUrl.trim()
    return !url || isValidUrl(url)
}

export function buildSearchQueryPayload(
    searchQuery: string,
    currentLandingPageUrl: string,
): SearchQueryPayload {
    const query = searchQuery.trim()
    const url = currentLandingPageUrl.trim()
    if (!query) throw new Error('Please enter a search query')
    if (url && !isValidUrl(url)) throw new Error('Please enter a valid URL')
    return {
        searchQuery: query,
        currentLandingPageUrl: url || undefined,
    }
}

export function SearchQueryForm({
    searchQuery,
    currentLandingPageUrl,
    onSearchQueryChange,
    onCurrentLandingPageUrlChange,
    disabled = false,
}: {
    searchQuery: string
    currentLandingPageUrl: string
    onSearchQueryChange: (value: string) => void
    onCurrentLandingPageUrlChange: (value: string) => void
    disabled?: boolean
}) {
    return (
        <div className="space-y-4">
            <div>
                <Label htmlFor="searchQuery">
                    Search Query
                    <span className="ml-1 text-destructive">*</span>
                </Label>
                <Input
                    id="searchQuery"
                    value={searchQuery}
                    onChange={(e) => onSearchQueryChange(e.target.value)}
                    placeholder="e.g., all in one coffee maker"
                    className="mt-2"
                    disabled={disabled}
                />
            </div>
            <div>
                <Label htmlFor="currentLandingPageUrl">
                    Current Landing Page URL
                </Label>
                <Input
                    id="currentLandingPageUrl"
                    type="url"
                    value={currentLandingPageUrl}
                    onChange={(e) =>
                        onCurrentLandingPageUrlChange(e.target.value)
                    }
                    placeholder="https://your-store.com/products/..."
                    className="mt-2"
                    disabled={disabled}
                />
            </div>
        </div>
    )
}

interface SearchQueryModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelected?: (payload: SearchQueryPayload) => void
    onSubmit?: (sessionId: string) => Promise<void>
    platform?: AdPlatform
    preselections?: AICreatePreselections
}

export function SearchQueryModal({
    open,
    onOpenChange,
    onSelected,
    onSubmit,
    platform = AD_PLATFORM.GOOGLE,
    preselections,
}: SearchQueryModalProps) {
    // All dismiss paths (overlay, Escape, X) go through Root.onOpenChange.
    // Gate them with a ref so a cancelled dismiss cannot unmount the content
    // while create-session is in flight and still fire onSubmit afterward.
    const submittingRef = useRef(false)
    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (!next && submittingRef.current) return
            onOpenChange(next)
        },
        [onOpenChange],
    )

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            {open && (
                <SearchQueryModalContent
                    onOpenChange={onOpenChange}
                    onSelected={onSelected}
                    onSubmit={onSubmit}
                    platform={platform}
                    preselections={preselections}
                    submittingRef={submittingRef}
                />
            )}
        </Dialog>
    )
}

function SearchQueryModalContent({
    onOpenChange,
    onSelected,
    onSubmit,
    platform,
    preselections,
    submittingRef,
}: Omit<SearchQueryModalProps, 'open'> & {
    submittingRef: MutableRefObject<boolean>
}) {
    const [searchQuery, setSearchQuery] = useState('')
    const [currentLandingPageUrl, setCurrentLandingPageUrl] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const canSubmit = canSubmitSearchQuery(searchQuery, currentLandingPageUrl)

    const handleClose = useCallback(() => {
        if (submittingRef.current) return
        onOpenChange(false)
    }, [submittingRef, onOpenChange])

    const handleSubmit = useCallback(async () => {
        let payload: SearchQueryPayload
        try {
            payload = buildSearchQueryPayload(
                searchQuery,
                currentLandingPageUrl,
            )
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Please enter a search query',
            )
            return
        }

        if (onSelected) {
            onSelected(payload)
            onOpenChange(false)
            return
        }
        if (!onSubmit) return

        submittingRef.current = true
        setIsSubmitting(true)
        try {
            const { sessionId } = await R.createAICreateSessionFromSearchQuery(
                payload.searchQuery,
                payload.currentLandingPageUrl,
                platform,
                undefined,
                undefined,
                preselections,
            )
            await onSubmit(sessionId)
            onOpenChange(false)
        } catch (error) {
            console.error('Failed to create search query session:', error)
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Failed to start from search query. Please try again.',
            )
        } finally {
            submittingRef.current = false
            setIsSubmitting(false)
        }
    }, [
        searchQuery,
        currentLandingPageUrl,
        onSelected,
        onSubmit,
        onOpenChange,
        platform,
        preselections,
        submittingRef,
    ])

    return (
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
                <DialogTitle>Add your search query</DialogTitle>
                <DialogDescription>
                    The Google Ads search query this storefront should target.
                </DialogDescription>
            </DialogHeader>

            <SearchQueryForm
                searchQuery={searchQuery}
                currentLandingPageUrl={currentLandingPageUrl}
                onSearchQueryChange={setSearchQuery}
                onCurrentLandingPageUrlChange={setCurrentLandingPageUrl}
                disabled={isSubmitting}
            />

            <div className="flex justify-end gap-3 pt-4">
                <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !canSubmit}
                    className="bg-[#276749] text-white hover:opacity-90"
                >
                    {isSubmitting ? 'Starting...' : 'Select'}
                </Button>
            </div>
        </DialogContent>
    )
}
